import {
  Component,
  EventEmitter,
  Input,
  Output,
  SimpleChanges,
  ViewChild,
} from "@angular/core";
import { MatTable } from "@angular/material/table";
import {
  faTimes,
  faSortUp,
  faSortDown,
} from "@fortawesome/free-solid-svg-icons";
import { AppState } from "src/app/store/state/app.state";
import { Store } from "@ngrx/store";
import { FACTSET_COPYRIGHT_MESSAGE } from "src/app/common-constants";
import {
  VAIconRegistryService,
  triangle,
  caretDown,
} from "@va/core-library/icons";
import { faCaretUp } from "@fortawesome/pro-solid-svg-icons";

@Component({
  selector: "app-comparison-grid",
  templateUrl: "./comparison-grid.component.html",
  styleUrls: ["./comparison-grid.component.scss"],
})
export class ComparisonGridComponent {
  @Input() isVisible = true;
  @Input() gridData;
  @Input() magnitudeMessage: string;
  @Input() showMedian: boolean;
  @Input() showAverage: boolean;
  @Input() showTotal: boolean;
  @Input() showFactsetMessage: boolean;
  @Input() orientation: string;
  @Input() hasFormulaBar: boolean;
  @Input() sortByColumnId: string;
  @Input() sortByDirection = "desc";
  @Input() isDataInProgress: boolean;
  @Input() showBlankRows = false;
  @Input() selectedCell: string;
  @Input() selectedRow: string;
  @Input() shouldShortDataOnChange: boolean;
  @Output() onDeleteTicker: EventEmitter<any> = new EventEmitter();
  @Output() handleCellClick: EventEmitter<any> = new EventEmitter();
  @Output() rowClick: EventEmitter<any> = new EventEmitter();
  @Output() updateTickerOrder: EventEmitter<any> = new EventEmitter();
  @ViewChild(MatTable) table: MatTable<any>;
  faTimes = faTimes;
  faSortUp = faSortUp;
  faSortDown = faSortDown;
  dataSource: Array<any> = [];
  displayedColumns = [];
  sortDirection = "";
  activeSort = "";
  factsetCopyRightMessage = FACTSET_COPYRIGHT_MESSAGE;
  fixedColumns = ["ticker", "companyName"];
  faCaretUp = faCaretUp;

  constructor(
    private iconRegistry: VAIconRegistryService,
    private store: Store<AppState>,
  ) {
    this.iconRegistry.registerIcons([caretDown, triangle]);
  }

  ngOnInit() {
    if (this.gridData && this.gridData.rowData) {
      this.activeSort = "";
    }
  }

  ngOnChanges(changes: SimpleChanges) {
    if (!this.isVisible) {
      this.selectedCell = "";
      this.selectedRow = "";
    }

    if (
      (changes.gridData &&
        changes.gridData.currentValue &&
        changes.gridData.currentValue !== changes.gridData.previousValue) ||
      (changes.showBlankRows &&
        changes.showBlankRows.currentValue !==
          changes.showBlankRows.previousValue)
    ) {
      this.displayedColumns = this.getDisplayColumns();
      if (this.gridData && this.gridData.rowData) {
        if (this.showBlankRows) {
          this.dataSource = [...this.gridData.rowData];
          setTimeout(() => {
            this.table.updateStickyColumnStyles();
          });
        } else {
          this.dataSource = [...this.gridData.rowData].filter(
            (item) => item.dataExists,
          );
        }
      } else {
        this.dataSource = undefined;
      }
    }
    if (this.sortByColumnId) {
      this.activeSort = this.sortByColumnId;
      this.sortDirection = this.sortByDirection;
      if (this.shouldShortDataOnChange) {
        this.sortData(this.sortByColumnId, this.sortByDirection);
      }
    }
  }

  getColumnParentKey(columnId) {
    if (this.gridData && this.gridData.dynamicColumns) {
      let subColumns = [];
      for (const item of this.gridData.dynamicColumns) {
        if (item.id === columnId) {
          return item.parentId;
        }
        if (item.subColumns && item.subColumns.length) {
          subColumns = subColumns.concat(item.subColumns);
        }
      }

      for (const item of subColumns) {
        if (item.id === columnId) {
          return item.parentId;
        }
      }
    }
    return undefined;
  }

  getDisplayColumns() {
    let result = [...this.fixedColumns];
    if (this.gridData && this.gridData.dynamicColumns) {
      result = [...result].concat(
        this.gridData.dynamicColumns.map((item) => item.id),
      );
    }
    return result;
  }

  /**
   * It handles ticker deletion from grid and raises onDeleteTicker event for the parent component to handle it
   * @param ticker ticker to be deleted
   */
  deleteTicker(ticker) {
    this.onDeleteTicker.emit(ticker);
  }

  /**
   * It is used to sort given column of the grid in given direction on peers tab
   * @param colKey key of the column to sort
   * @param sortDirection whether to sort asc or desc directio
   */
  sortData(colKey, sortDirection = "") {
    if (sortDirection) {
      this.sortDirection = sortDirection;
    } else {
      if (colKey === this.activeSort) {
        this.sortDirection =
          this.sortDirection === ""
            ? "asc"
            : this.sortDirection === "asc"
              ? "desc"
              : "";
      } else {
        this.sortDirection = "asc";
      }
    }
    this.activeSort = colKey;
    switch (colKey) {
      case "companyName":
        if (this.sortDirection === "") {
          if (this.showBlankRows) {
            this.dataSource = [...this.gridData.rowData];
          } else {
            this.dataSource = [...this.gridData.rowData].filter(
              (item) => item.dataExists,
            );
          }
          this.activeSort = "";
        } else {
          this.dataSource.sort((a, b) => {
            if (this.sortDirection === "asc") {
              return a.companyName.localeCompare(b.companyName);
            }

            return b.companyName.localeCompare(a.companyName);
          });
        }
        this.table.renderRows();
        break;
      case "relative_period_Y1":
        if (this.sortDirection === "") {
          if (this.showBlankRows) {
            this.dataSource = [...this.gridData.rowData];
          } else {
            this.dataSource = [...this.gridData.rowData].filter(
              (item) => item.dataExists,
            );
          }
          this.activeSort = "";
        } else {
          this.dataSource.sort((a, b) => {
            const firstValue = a[colKey] && a[colKey].raw ? a[colKey].raw : "";
            const secondValue = b[colKey] && b[colKey].raw ? b[colKey].raw : "";
            if (this.sortDirection === "asc") {
              return firstValue.localeCompare(secondValue);
            }
            return secondValue.localeCompare(firstValue);
          });
        }
        this.table.renderRows();
        break;
      case "last_earnings_date":
      case "next_earnings_date":
        if (this.sortDirection === "") {
          if (this.showBlankRows) {
            this.dataSource = [...this.gridData.rowData];
          } else {
            this.dataSource = [...this.gridData.rowData].filter(
              (item) => item.dataExists,
            );
          }
          this.activeSort = "";
        } else {
          this.dataSource.sort((a, b) => {
            const firstValue = a[colKey] ? a[colKey].raw : undefined;
            const secondValue = b[colKey] ? b[colKey].raw : undefined;
            if (this.sortDirection === "asc") {
              if (firstValue === undefined && secondValue === undefined) {
                return 1;
              }
              if (firstValue === undefined) {
                return -1;
              }
              if (secondValue === undefined) {
                return 1;
              }
              return (
                new Date(firstValue).valueOf() - new Date(secondValue).valueOf()
              );
            }
            if (firstValue === undefined) {
              return 1;
            }
            if (secondValue === undefined) {
              return -1;
            }
            return (
              new Date(secondValue).valueOf() - new Date(firstValue).valueOf()
            );
          });
        }
        this.table.renderRows();
        break;
      default:
        //sort for data columns
        if (this.sortDirection === "") {
          if (this.showBlankRows) {
            this.dataSource = [...this.gridData.rowData];
          } else {
            this.dataSource = [...this.gridData.rowData].filter(
              (item) => item.dataExists,
            );
          }
          this.activeSort = "";
        } else {
          this.dataSource.sort((a, b) => {
            const aSliData = this.gridData.columnParentMapping[colKey]
              ? a[this.gridData.columnParentMapping[colKey]] &&
                a[this.gridData.columnParentMapping[colKey]][colKey]
                ? a[this.gridData.columnParentMapping[colKey]][colKey]["raw"]
                : undefined
              : a[colKey]
                ? a[colKey]["raw"]
                : undefined;
            const bSliData = this.gridData.columnParentMapping[colKey]
              ? b[this.gridData.columnParentMapping[colKey]] &&
                b[this.gridData.columnParentMapping[colKey]][colKey]
                ? b[this.gridData.columnParentMapping[colKey]][colKey]["raw"]
                : undefined
              : b[colKey]
                ? b[colKey]["raw"]
                : undefined;

            if (this.sortDirection === "asc") {
              if (aSliData === undefined && bSliData === undefined) {
                return 1;
              }
              if (aSliData === undefined) {
                return -1;
              }
              if (bSliData === undefined) {
                return 1;
              }
              if (aSliData === "NM" && bSliData === "NM") {
                return 1;
              }
              if (aSliData === "NM") {
                return -1;
              }
              if (bSliData === "NM") {
                return 1;
              }
              return aSliData - bSliData;
            }
            if (aSliData === undefined) {
              return 1;
            }
            if (bSliData === undefined) {
              return -1;
            }
            if (aSliData === "NM") {
              return 1;
            }
            if (bSliData === "NM") {
              return -1;
            }
            return bSliData - aSliData;
          });
        }
        this.table && this.table.renderRows();
        break;
    }
    setTimeout(() => {
      this.table.updateStickyColumnStyles();
    });
    this.updateTickerOrder.emit(this.dataSource);
  }

  /**
   * It is used to highlight the currently selected row on grid
   * @param rowId identifier of the selected row
   */
  onRowClick(rowId) {
    this.rowClick.emit(rowId);
  }

  /**
   * It is used to highlight seleted cell on the grid.
   * @param event click event
   * @param ticker ticker related to the data point
   * @param columnId identifier of the column
   */
  onCellClick(event: MouseEvent, ticker, columnId, parentId) {
    const hasFB = this.hasFormulaBar;
    const paramData = { ticker, columnId, parentId, hasFB };
    this.handleCellClick.emit(paramData);
    if (event) {
      event.stopPropagation(); // prevent onRowClick
    }
  }
}
